import { useState } from "react";

export interface ViewOption<V extends string> { id: V; label: string }

/** Which layout a list page shows (board / table / cards), remembered per viewer in this browser. */
export function useListView<V extends string>(key: string, options: readonly V[], fallback: V) {
  const storageKey = `list_view_${key}`;
  const [view, setView] = useState<V>(() => {
    try {
      const v = localStorage.getItem(storageKey) as V | null;
      return v && options.includes(v) ? v : fallback;
    } catch { return fallback; }
  });
  const choose = (v: V) => {
    setView(v);
    try { localStorage.setItem(storageKey, v); } catch { /* per-viewer convenience only */ }
  };
  return [view, choose] as const;
}

/** The pill switch shown in the page header, styled like the Invoices tabs. */
export default function ViewToggle<V extends string>({ view, onChange, options }: { view: V; onChange: (v: V) => void; options: ViewOption<V>[] }) {
  return (
    <div className="inline-flex rounded-full bg-gray-100 p-1 text-xs">
      {options.map((o) => (
        <button key={o.id} type="button" onClick={() => onChange(o.id)} className={`px-3 sm:px-4 py-1 rounded-full transition ${view === o.id ? "bg-white shadow-sm text-gray-900 font-medium" : "text-gray-600"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
